import { useTranslation } from 'react-i18next';
import type { BatchReleaseMode } from '@ooh/contracts';
import { Button } from '@ooh/ui/components/button';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { formatDisplayDateRange } from '@ooh/ui/lib/date-format';
import type { OutcomeResultUnitLookup } from '../../reservations/components/outcome-result-buckets';
import { ReleaseModeChoice } from '../../reservations/components/unified-select-release-mode-choice';
import type { ResolvedSelection } from './popover-types';
import {
  ReleaseBreakdownView,
  type ReleaseBreakdownPreview,
} from './unified-select-release-breakdown-view';

type ReleasableCell = ResolvedSelection['releasableCells'][number];

export interface ReleaseModeChoiceState {
  anyUnderCovered: boolean;
  mode: BatchReleaseMode | null;
  disabled: boolean;
  onChange: (mode: BatchReleaseMode) => void;
}

export interface ReleaseConfirmDialogProps {
  singleReleaseCell: ReleasableCell | undefined;
  preview: ReleaseBreakdownPreview;
  lookup: OutcomeResultUnitLookup;
  resolveMediaUnitLabel: ((mediaUnitId: number) => string | undefined) | undefined;
  modeChoice: ReleaseModeChoiceState;
  submitDisabled: boolean;
  onSubmit: () => void;
  onClose: () => void;
}

/**
 * The exactly-one-releasable-cell case names the offer, media unit and date range being released
 * instead of routing through the general breakdown groups.
 */
function SingleReleaseSentence({
  cell,
  resolveMediaUnitLabel,
}: {
  cell: ReleasableCell;
  resolveMediaUnitLabel: ((mediaUnitId: number) => string | undefined) | undefined;
}) {
  const { t } = useTranslation('inventory');
  return (
    <p className="text-sm text-muted-foreground">
      {t('availability.select.confirmReleaseSingleBody', {
        offerName:
          cell.ownReservation.offerTitle ?? t('availability.select.confirmReleaseUnknownOffer'),
        mediaUnit:
          resolveMediaUnitLabel?.(cell.mediaUnitId) ??
          t('availability.select.confirmReleaseUnknownUnit'),
        dateRange: formatDisplayDateRange(cell.dateFrom, cell.dateTo),
      })}
    </p>
  );
}

/**
 * While an under-covered selection has no mode picked, only the whole-versus-selected-period
 * choice renders and no breakdown request has been fired. The single-cell sentence replaces the
 * general breakdown only once the breakdown itself has resolved.
 */
function ReleaseConfirmBody({
  singleReleaseCell,
  preview,
  lookup,
  resolveMediaUnitLabel,
  modeChoice,
}: Omit<ReleaseConfirmDialogProps, 'submitDisabled' | 'onSubmit' | 'onClose'>) {
  const { t } = useTranslation('inventory');
  const choiceNode = modeChoice.anyUnderCovered ? (
    <ReleaseModeChoice
      value={modeChoice.mode}
      onChange={modeChoice.onChange}
      disabled={modeChoice.disabled}
      labels={{
        title: t('availability.select.releaseModeChoice.title'),
        wholeLabel: t('availability.select.releaseModeChoice.wholeLabel'),
        selectedLabel: t('availability.select.releaseModeChoice.selectedLabel'),
      }}
    />
  ) : null;

  if (modeChoice.anyUnderCovered && modeChoice.mode === null) return choiceNode;

  const breakdownSettled = !preview.isLoading && !preview.isError;
  return (
    <>
      {choiceNode}
      {singleReleaseCell && breakdownSettled ? (
        <SingleReleaseSentence
          cell={singleReleaseCell}
          resolveMediaUnitLabel={resolveMediaUnitLabel}
        />
      ) : (
        <ReleaseBreakdownView preview={preview} lookup={lookup} />
      )}
    </>
  );
}

export function ReleaseConfirmDialog({
  submitDisabled,
  onSubmit,
  onClose,
  ...bodyProps
}: ReleaseConfirmDialogProps) {
  const { t } = useTranslation('inventory');
  return (
    <ModalDialog
      testId="unified-select-release-confirm"
      header={
        <span className="text-base font-semibold">
          {t('availability.select.confirmReleaseTitle')}
        </span>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="unified-select-release-confirm-cancel"
            onClick={onClose}
          >
            {t('availability.select.confirmReleaseCancelBtn')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={submitDisabled}
            data-testid="unified-select-release-confirm-submit"
            onClick={onSubmit}
          >
            {t('availability.select.confirmReleaseSubmitBtn')}
          </Button>
        </div>
      }
      onClose={onClose}
    >
      <ReleaseConfirmBody {...bodyProps} />
    </ModalDialog>
  );
}
